import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Calendar, 
  Droplet, 
  Heart, 
  Zap, 
  Brain, 
  ChevronLeft, 
  ChevronRight,
  Plus,
  X,
  Check,
  TrendingUp,
  AlertCircle,
  Sparkles
} from "lucide-react";
import { GlassCard } from "./GlassCard";
import { Button } from "./ui/button";
import axios from "axios";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Phase colors
const PHASE_COLORS = {
  menstrual: { bg: "bg-red-500/20", border: "border-red-500/50", text: "text-red-400", label: "Menstrual" },
  follicular: { bg: "bg-green-500/20", border: "border-green-500/50", text: "text-green-400", label: "Folicular" },
  ovulation: { bg: "bg-yellow-500/20", border: "border-yellow-500/50", text: "text-yellow-400", label: "Ovulación" },
  luteal: { bg: "bg-purple-500/20", border: "border-purple-500/50", text: "text-purple-400", label: "Lútea" }
};

// Flow intensity icons
const FLOW_ICONS = {
  none: { drops: 0, color: "text-slate-500" },
  spotting: { drops: 1, color: "text-pink-300" },
  light: { drops: 2, color: "text-pink-400" },
  medium: { drops: 3, color: "text-red-400" },
  heavy: { drops: 4, color: "text-red-500" }
};

// Mini calendar component
const MiniCalendar = ({ entries, currentMonth, onDateSelect, selectedDate, predictions }) => {
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    
    return { daysInMonth, startingDay };
  };

  const { daysInMonth, startingDay } = getDaysInMonth(currentMonth);
  const days = [];
  
  // Create a map of predicted dates for quick lookup
  const predictedDatesMap = {};
  if (predictions?.predicted_dates) {
    predictions.predicted_dates.forEach(p => {
      predictedDatesMap[p.date] = p;
    });
  }
  
  // Empty cells for days before month starts
  for (let i = 0; i < startingDay; i++) {
    days.push(<div key={`empty-${i}`} className="w-8 h-8" />);
  }
  
  // Days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const entry = entries.find(e => e.date === dateStr);
    const prediction = predictedDatesMap[dateStr];
    const isSelected = selectedDate === dateStr;
    const isToday = dateStr === new Date().toISOString().split('T')[0];
    
    const phaseColor = entry?.phase ? PHASE_COLORS[entry.phase] : null;
    const hasFlow = entry?.flow_intensity && entry.flow_intensity !== 'none';
    
    // Determine visual style based on entry or prediction
    let bgClass = "hover:bg-white/10";
    let textClass = "text-slate-400";
    let borderClass = "";
    let isPredicted = false;
    
    if (entry?.phase && phaseColor) {
      bgClass = phaseColor.bg;
      textClass = phaseColor.text;
    } else if (prediction) {
      isPredicted = true;
      if (prediction.type === "period") {
        bgClass = "bg-red-500/10";
        textClass = "text-red-300";
        borderClass = "border border-dashed border-red-500/30";
      } else if (prediction.is_ovulation) {
        bgClass = "bg-yellow-500/10";
        textClass = "text-yellow-300";
        borderClass = "border border-dashed border-yellow-500/30";
      } else if (prediction.type === "fertile_window" || prediction.type === "fertile") {
        bgClass = "bg-cyan-500/10";
        textClass = "text-cyan-300";
        borderClass = "border border-dashed border-cyan-500/20";
      }
    }
    
    days.push(
      <button
        key={day}
        onClick={() => onDateSelect(dateStr)}
        className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-rajdhani transition-all relative
          ${isSelected ? 'ring-2 ring-pink-400 ring-offset-1 ring-offset-void' : ''}
          ${isToday ? 'font-bold' : ''}
          ${bgClass} ${textClass} ${borderClass}
        `}
        title={isPredicted ? (prediction.type === "period" ? "Período predicho" : prediction.is_ovulation ? "Ovulación predicha" : "Ventana fértil") : ""}
      >
        {day}
        {hasFlow && (
          <span className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-red-400" />
        )}
        {isPredicted && prediction.is_ovulation && (
          <span className="absolute -top-0.5 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-yellow-400" />
        )}
      </button>
    );
  }

  return (
    <div className="grid grid-cols-7 gap-1">
      {['D', 'L', 'M', 'X', 'J', 'V', 'S'].map(d => (
        <div key={d} className="w-8 h-8 flex items-center justify-center text-xs text-slate-500 font-rajdhani">
          {d}
        </div>
      ))}
      {days}
    </div>
  );
};

// Entry form component
const EntryForm = ({ date, existingEntry, symptoms, flowOptions, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    cycle_day: existingEntry?.cycle_day || null,
    phase: existingEntry?.phase || null,
    flow_intensity: existingEntry?.flow_intensity || 'none',
    symptoms: existingEntry?.symptoms || {},
    energy_level: existingEntry?.energy_level || null,
    notes: existingEntry?.notes || ''
  });
  const [saving, setSaving] = useState(false);

  const handleSymptomChange = (symptom, value) => {
    setFormData(prev => ({
      ...prev,
      symptoms: { ...prev.symptoms, [symptom]: value }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({ ...formData, date });
      onClose();
    } catch (error) {
      toast.error("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr + 'T12:00:00');
    return d.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div 
        className="bg-void-paper border border-white/10 rounded-xl max-w-lg w-full max-h-[85vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-4 border-b border-white/10 flex items-center justify-between sticky top-0 bg-void-paper z-10">
          <div>
            <h3 className="font-orbitron text-white">Registro del Ciclo</h3>
            <p className="text-xs text-slate-400 capitalize">{formatDate(date)}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg">
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        <div className="p-4 space-y-5">
          {/* Phase Selection */}
          <div>
            <label className="text-sm text-slate-400 mb-2 block">Fase del ciclo</label>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(PHASE_COLORS).map(([key, value]) => (
                <button
                  key={key}
                  onClick={() => setFormData({ ...formData, phase: formData.phase === key ? null : key })}
                  className={`px-3 py-2 rounded-lg text-sm font-rajdhani transition-all ${
                    formData.phase === key
                      ? `${value.bg} ${value.border} border ${value.text}`
                      : "bg-white/5 border border-white/10 text-slate-400 hover:border-white/20"
                  }`}
                >
                  {value.label}
                </button>
              ))}
            </div>
          </div>

          {/* Flow Intensity */}
          <div>
            <label className="text-sm text-slate-400 mb-2 block flex items-center gap-2">
              <Droplet className="w-4 h-4 text-red-400" />
              Intensidad del flujo
            </label>
            <div className="flex gap-2">
              {Object.entries(flowOptions || {}).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setFormData({ ...formData, flow_intensity: key })}
                  className={`flex-1 px-2 py-2 rounded-lg text-xs font-rajdhani transition-all flex flex-col items-center gap-1 ${
                    formData.flow_intensity === key
                      ? "bg-red-500/20 border border-red-500/50 text-red-400"
                      : "bg-white/5 border border-white/10 text-slate-400 hover:border-white/20"
                  }`}
                >
                  <div className="flex gap-0.5">
                    {[...Array(FLOW_ICONS[key]?.drops || 0)].map((_, i) => (
                      <Droplet key={i} className={`w-3 h-3 ${FLOW_ICONS[key]?.color}`} fill="currentColor" />
                    ))}
                    {(FLOW_ICONS[key]?.drops || 0) === 0 && <span className="text-slate-500">-</span>}
                  </div>
                  <span className="truncate">{label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Energy Level */}
          <div>
            <label className="text-sm text-slate-400 mb-2 block flex items-center gap-2">
              <Zap className="w-4 h-4 text-yellow-400" />
              Nivel de energía
            </label>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map(level => (
                <button
                  key={level}
                  onClick={() => setFormData({ ...formData, energy_level: formData.energy_level === level ? null : level })}
                  className={`flex-1 py-3 rounded-lg text-sm font-rajdhani transition-all ${
                    formData.energy_level === level
                      ? level <= 2 ? "bg-red-500/20 border border-red-500/50 text-red-400"
                        : level === 3 ? "bg-yellow-500/20 border border-yellow-500/50 text-yellow-400"
                        : "bg-green-500/20 border border-green-500/50 text-green-400"
                      : "bg-white/5 border border-white/10 text-slate-400 hover:border-white/20"
                  }`}
                >
                  {level}
                </button>
              ))}
            </div>
            <p className="text-xs text-slate-600 mt-1 text-center">1 = Muy baja, 5 = Muy alta</p>
          </div>

          {/* Symptoms */}
          <div>
            <label className="text-sm text-slate-400 mb-2 block flex items-center gap-2">
              <Heart className="w-4 h-4 text-pink-400" />
              Síntomas (toca para indicar severidad 1-5)
            </label>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(symptoms || {}).map(([key, label]) => {
                const severity = formData.symptoms[key] || 0;
                return (
                  <button
                    key={key}
                    onClick={() => {
                      const newSeverity = severity >= 5 ? 0 : severity + 1;
                      handleSymptomChange(key, newSeverity);
                    }}
                    className={`px-3 py-2 rounded-lg text-xs font-rajdhani transition-all flex items-center justify-between ${
                      severity > 0
                        ? "bg-pink-500/20 border border-pink-500/50 text-pink-400"
                        : "bg-white/5 border border-white/10 text-slate-400 hover:border-white/20"
                    }`}
                  >
                    <span className="truncate">{label}</span>
                    {severity > 0 && (
                      <span className="ml-2 px-1.5 py-0.5 rounded bg-pink-500/30 text-pink-300 text-xs">
                        {severity}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="text-sm text-slate-400 mb-2 block">Notas (opcional)</label>
            <textarea
              value={formData.notes}
              onChange={e => setFormData({ ...formData, notes: e.target.value })}
              placeholder="¿Cómo te sientes hoy?"
              className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm font-inter resize-none h-20 focus:outline-none focus:border-pink-500/50"
            />
          </div>

          {/* Save Button */}
          <Button
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-gradient-to-r from-pink-500/30 to-purple-500/30 border border-pink-500/50 text-pink-300 hover:from-pink-500/40 hover:to-purple-500/40"
          >
            {saving ? "Guardando..." : "Guardar Registro"}
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

// Analysis component
const CycleAnalysis = ({ analysis, aiInsights }) => {
  if (!analysis) return null;

  return (
    <GlassCard className="p-4 xl:p-6">
      <h3 className="font-orbitron text-lg text-white mb-4 flex items-center gap-2">
        <TrendingUp className="w-5 h-5 text-pink-400" />
        Análisis de tu Ciclo
      </h3>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        {/* Mood by Phase */}
        <div className="p-3 rounded-lg bg-white/5 border border-white/10">
          <p className="text-xs text-slate-400 mb-2">Ánimo por fase</p>
          <div className="space-y-1">
            {Object.entries(analysis.mood_by_phase || {}).map(([phase, score]) => (
              <div key={phase} className="flex items-center justify-between">
                <span className={`text-xs ${PHASE_COLORS[phase]?.text || 'text-slate-400'}`}>
                  {PHASE_COLORS[phase]?.label || phase}
                </span>
                <div className="flex items-center gap-1">
                  <div className="w-16 h-1.5 bg-white/10 rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full ${PHASE_COLORS[phase]?.bg?.replace('/20', '/60') || 'bg-slate-500'}`}
                      style={{ width: `${(score / 5) * 100}%` }}
                    />
                  </div>
                  <span className="text-xs text-slate-300 w-6 text-right">{score}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Energy by Phase */}
        <div className="p-3 rounded-lg bg-white/5 border border-white/10">
          <p className="text-xs text-slate-400 mb-2">Energía por fase</p>
          <div className="space-y-1">
            {Object.entries(analysis.energy_by_phase || {}).map(([phase, level]) => (
              <div key={phase} className="flex items-center justify-between">
                <span className={`text-xs ${PHASE_COLORS[phase]?.text || 'text-slate-400'}`}>
                  {PHASE_COLORS[phase]?.label || phase}
                </span>
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Zap 
                      key={i} 
                      className={`w-3 h-3 ${i < Math.round(level) ? 'text-yellow-400' : 'text-slate-600'}`}
                      fill={i < Math.round(level) ? 'currentColor' : 'none'}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Top Symptoms */}
      {analysis.top_symptoms?.length > 0 && (
        <div className="mb-6">
          <p className="text-xs text-slate-400 mb-2">Síntomas más frecuentes</p>
          <div className="flex flex-wrap gap-2">
            {analysis.top_symptoms.map((s, i) => (
              <span 
                key={s.symptom}
                className="px-2 py-1 rounded-full bg-pink-500/10 border border-pink-500/30 text-pink-400 text-xs"
              >
                {s.label} ({s.days} días)
              </span>
            ))}
          </div>
        </div>
      )}

      {/* AI Insights */}
      {aiInsights && (
        <div className="p-4 rounded-lg bg-gradient-to-r from-pink-500/10 to-purple-500/10 border border-pink-500/20">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-pink-400" />
            <span className="text-sm font-rajdhani text-pink-400">Insights de IA</span>
          </div>
          <div className="text-sm text-slate-300 leading-relaxed prose prose-invert prose-sm max-w-none">
            <ReactMarkdown
              components={{
                p: ({ children }) => <p className="mb-2">{children}</p>,
                ul: ({ children }) => <ul className="list-none space-y-1 mb-2">{children}</ul>,
                li: ({ children }) => <li className="flex items-start gap-2"><span className="text-pink-400">•</span><span>{children}</span></li>,
                strong: ({ children }) => <strong className="text-white font-semibold">{children}</strong>,
              }}
            >
              {aiInsights}
            </ReactMarkdown>
          </div>
        </div>
      )}
    </GlassCard>
  );
};

// Period Setup Modal
const PeriodSetupModal = ({ onSave, onClose }) => {
  const [lastPeriodStart, setLastPeriodStart] = useState(new Date().toISOString().split('T')[0]);
  const [avgCycleLength, setAvgCycleLength] = useState(28);
  const [avgPeriodLength, setAvgPeriodLength] = useState(5);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        last_period_start: lastPeriodStart,
        average_cycle_length: avgCycleLength,
        average_period_length: avgPeriodLength
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-void-paper border border-white/10 rounded-xl max-w-md w-full p-6"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-orbitron text-white text-lg">Configurar Ciclo</h3>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg">
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        <div className="space-y-5">
          {/* Last Period Start */}
          <div>
            <label className="text-sm text-slate-400 mb-2 block">
              ¿Cuándo empezó tu último período?
            </label>
            <input
              type="date"
              value={lastPeriodStart}
              onChange={e => setLastPeriodStart(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
              className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white font-inter focus:outline-none focus:border-pink-500/50"
            />
          </div>

          {/* Average Cycle Length */}
          <div>
            <label className="text-sm text-slate-400 mb-2 block">
              Duración promedio de tu ciclo (días)
            </label>
            <div className="flex items-center gap-3">
              <input
                type="range"
                min="21"
                max="45"
                value={avgCycleLength}
                onChange={e => setAvgCycleLength(parseInt(e.target.value))}
                className="flex-1 accent-pink-500"
              />
              <span className="text-white font-rajdhani w-12 text-center">{avgCycleLength}</span>
            </div>
            <p className="text-xs text-slate-500 mt-1">Normal: 21-35 días</p>
          </div>

          {/* Average Period Length */}
          <div>
            <label className="text-sm text-slate-400 mb-2 block">
              Duración promedio de tu período (días)
            </label>
            <div className="flex items-center gap-3">
              <input
                type="range"
                min="2"
                max="10"
                value={avgPeriodLength}
                onChange={e => setAvgPeriodLength(parseInt(e.target.value))}
                className="flex-1 accent-pink-500"
              />
              <span className="text-white font-rajdhani w-12 text-center">{avgPeriodLength}</span>
            </div>
            <p className="text-xs text-slate-500 mt-1">Normal: 3-7 días</p>
          </div>

          {/* Save Button */}
          <Button
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-gradient-to-r from-pink-500/30 to-purple-500/30 border border-pink-500/50 text-pink-300 hover:from-pink-500/40 hover:to-purple-500/40"
          >
            {saving ? "Guardando..." : "Guardar y Ver Predicciones"}
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Main Cycle Diary Component
export const CycleDiary = () => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [entries, setEntries] = useState([]);
  const [symptoms, setSymptoms] = useState({});
  const [flowOptions, setFlowOptions] = useState({});
  const [showEntryForm, setShowEntryForm] = useState(false);
  const [showPeriodSetup, setShowPeriodSetup] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [aiInsights, setAiInsights] = useState(null);
  const [predictions, setPredictions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingCalendar, setDownloadingCalendar] = useState(false);

  // Handle fertile window calendar download
  const handleFertileCalendarDownload = async () => {
    setDownloadingCalendar(true);
    try {
      const response = await axios.get(`${API}/cycle/fertile-calendar.ics`, {
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'text/calendar' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'moodpunk-ventana-fertil.ics';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('¡Calendario descargado! Ábrelo para añadir los eventos a tu calendario');
    } catch (error) {
      console.error('Error downloading calendar:', error);
      if (error.response?.status === 400) {
        toast.error('Configura tu ciclo primero para generar el calendario');
      } else {
        toast.error('Error al descargar el calendario');
      }
    } finally {
      setDownloadingCalendar(false);
    }
  };

  const fetchData = useCallback(async () => {
    try {
      // Fetch options, entries, analysis, and predictions in parallel
      const [optionsRes, entriesRes, analysisRes, predictionsRes] = await Promise.all([
        axios.get(`${API}/cycle/symptoms-options`),
        axios.get(`${API}/cycle/entries?limit=90`),
        axios.get(`${API}/cycle/analysis`),
        axios.get(`${API}/cycle/predictions`)
      ]);

      setSymptoms(optionsRes.data.symptoms || {});
      setFlowOptions(optionsRes.data.flow_intensity || {});
      setEntries(entriesRes.data.entries || []);
      setAnalysis(analysisRes.data.analysis);
      setAiInsights(analysisRes.data.ai_insights);
      setPredictions(predictionsRes.data.predictions);
    } catch (error) {
      console.error("Error fetching cycle data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSaveEntry = async (entryData) => {
    try {
      await axios.post(`${API}/cycle/entry`, entryData);
      toast.success("Registro guardado");
      fetchData();
    } catch (error) {
      console.error("Error saving entry:", error);
      throw error;
    }
  };

  const navigateMonth = (direction) => {
    setCurrentMonth(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + direction);
      return newDate;
    });
  };

  const selectedEntry = entries.find(e => e.date === selectedDate);

  const formatMonth = (date) => {
    return date.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-pink-500/30 border-t-pink-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-orbitron text-xl text-white flex items-center gap-2">
            <Calendar className="w-6 h-6 text-pink-400" />
            Diario de Ciclo
          </h2>
          <p className="text-sm text-slate-400">Registra y analiza tus patrones menstruales</p>
        </div>
        <Button
          onClick={() => setShowEntryForm(true)}
          className="bg-pink-500/20 border border-pink-500/50 text-pink-400 hover:bg-pink-500/30"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nuevo registro
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Calendar */}
        <GlassCard className="p-4 xl:p-6">
          {/* Month Navigation */}
          <div className="flex items-center justify-between mb-4">
            <button 
              onClick={() => navigateMonth(-1)}
              className="p-2 hover:bg-white/10 rounded-lg"
            >
              <ChevronLeft className="w-5 h-5 text-slate-400" />
            </button>
            <span className="font-rajdhani text-white capitalize">{formatMonth(currentMonth)}</span>
            <button 
              onClick={() => navigateMonth(1)}
              className="p-2 hover:bg-white/10 rounded-lg"
            >
              <ChevronRight className="w-5 h-5 text-slate-400" />
            </button>
          </div>

          <MiniCalendar
            entries={entries}
            currentMonth={currentMonth}
            selectedDate={selectedDate}
            onDateSelect={setSelectedDate}
            predictions={predictions}
          />

          {/* Phase Legend */}
          <div className="mt-4 pt-4 border-t border-white/10">
            <p className="text-xs text-slate-500 mb-2">Leyenda de fases</p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(PHASE_COLORS).map(([key, value]) => (
                <span key={key} className={`px-2 py-1 rounded-full text-xs ${value.bg} ${value.text}`}>
                  {value.label}
                </span>
              ))}
            </div>
            {/* Prediction legend */}
            {predictions && (
              <div className="mt-2 pt-2 border-t border-white/5">
                <p className="text-xs text-slate-500 mb-2">Predicciones</p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-2 py-1 rounded-full text-xs bg-red-500/10 border border-dashed border-red-500/30 text-red-300">
                    Período
                  </span>
                  <span className="px-2 py-1 rounded-full text-xs bg-yellow-500/10 border border-dashed border-yellow-500/30 text-yellow-300">
                    Ovulación
                  </span>
                  <span className="px-2 py-1 rounded-full text-xs bg-cyan-500/10 border border-dashed border-cyan-500/20 text-cyan-300">
                    Fértil
                  </span>
                </div>
              </div>
            )}
          </div>
        </GlassCard>

        {/* Selected Day Info */}
        <GlassCard className="p-4 xl:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-rajdhani text-white">
              {new Date(selectedDate + 'T12:00:00').toLocaleDateString('es-ES', { 
                weekday: 'long', 
                day: 'numeric', 
                month: 'long' 
              })}
            </h3>
            <button
              onClick={() => setShowEntryForm(true)}
              className="text-xs text-pink-400 hover:text-pink-300"
            >
              {selectedEntry ? 'Editar' : 'Añadir'}
            </button>
          </div>

          {selectedEntry ? (
            <div className="space-y-4">
              {/* Phase & Flow */}
              <div className="flex gap-3">
                {selectedEntry.phase && (
                  <span className={`px-3 py-1.5 rounded-lg text-sm ${PHASE_COLORS[selectedEntry.phase]?.bg} ${PHASE_COLORS[selectedEntry.phase]?.border} border ${PHASE_COLORS[selectedEntry.phase]?.text}`}>
                    {PHASE_COLORS[selectedEntry.phase]?.label}
                  </span>
                )}
                {selectedEntry.flow_intensity && selectedEntry.flow_intensity !== 'none' && (
                  <span className="px-3 py-1.5 rounded-lg text-sm bg-red-500/20 border border-red-500/50 text-red-400 flex items-center gap-1">
                    {[...Array(FLOW_ICONS[selectedEntry.flow_intensity]?.drops || 0)].map((_, i) => (
                      <Droplet key={i} className="w-3 h-3" fill="currentColor" />
                    ))}
                    {flowOptions[selectedEntry.flow_intensity]}
                  </span>
                )}
              </div>

              {/* Energy & Mood */}
              <div className="grid grid-cols-2 gap-3">
                {selectedEntry.energy_level && (
                  <div className="p-3 rounded-lg bg-white/5 border border-white/10">
                    <p className="text-xs text-slate-500 mb-1">Energía</p>
                    <div className="flex gap-1">
                      {[...Array(5)].map((_, i) => (
                        <Zap 
                          key={i} 
                          className={`w-4 h-4 ${i < selectedEntry.energy_level ? 'text-yellow-400' : 'text-slate-600'}`}
                          fill={i < selectedEntry.energy_level ? 'currentColor' : 'none'}
                        />
                      ))}
                    </div>
                  </div>
                )}
                {selectedEntry.mood_score && (
                  <div className="p-3 rounded-lg bg-white/5 border border-white/10">
                    <p className="text-xs text-slate-500 mb-1">Ánimo</p>
                    <div className="flex gap-1">
                      {[...Array(5)].map((_, i) => (
                        <Heart 
                          key={i} 
                          className={`w-4 h-4 ${i < selectedEntry.mood_score ? 'text-pink-400' : 'text-slate-600'}`}
                          fill={i < selectedEntry.mood_score ? 'currentColor' : 'none'}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Symptoms */}
              {selectedEntry.symptoms && Object.keys(selectedEntry.symptoms).some(k => selectedEntry.symptoms[k] > 0) && (
                <div>
                  <p className="text-xs text-slate-500 mb-2">Síntomas</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(selectedEntry.symptoms).map(([key, value]) => 
                      value > 0 && (
                        <span key={key} className="px-2 py-1 rounded-full bg-pink-500/10 border border-pink-500/30 text-pink-400 text-xs">
                          {symptoms[key]} ({value}/5)
                        </span>
                      )
                    )}
                  </div>
                </div>
              )}

              {/* Notes */}
              {selectedEntry.notes && (
                <div className="p-3 rounded-lg bg-white/5 border border-white/10">
                  <p className="text-xs text-slate-500 mb-1">Notas</p>
                  <p className="text-sm text-slate-300">{selectedEntry.notes}</p>
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <AlertCircle className="w-8 h-8 text-slate-600 mb-3" />
              <p className="text-slate-500 text-sm">No hay registro para este día</p>
              <button
                onClick={() => setShowEntryForm(true)}
                className="mt-3 text-pink-400 text-sm hover:text-pink-300"
              >
                + Añadir registro
              </button>
            </div>
          )}
        </GlassCard>
      </div>

      {/* Predictions Card */}
      {predictions && (
        <GlassCard className="p-4 xl:p-6">
          <h3 className="font-orbitron text-lg text-white mb-4 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-yellow-400" />
            Predicciones de tu Ciclo
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Current Cycle Status */}
            <div className="p-4 rounded-lg bg-gradient-to-r from-pink-500/10 to-purple-500/10 border border-pink-500/20">
              <p className="text-xs text-slate-400 mb-1">Estado actual</p>
              <p className="text-2xl font-orbitron text-pink-400">
                Día {predictions.current_cycle?.day}
              </p>
              <p className="text-sm text-slate-300 capitalize">
                Fase {PHASE_COLORS[predictions.current_cycle?.phase]?.label || predictions.current_cycle?.phase}
              </p>
            </div>
            
            {/* Next Period */}
            <div className="p-4 rounded-lg bg-gradient-to-r from-red-500/10 to-pink-500/10 border border-red-500/20">
              <p className="text-xs text-slate-400 mb-1">Próximo período</p>
              {predictions.next_period?.days_until <= 3 ? (
                <p className="text-2xl font-orbitron text-red-400">
                  {predictions.next_period?.days_until === 0 ? '¡Hoy!' : 
                   predictions.next_period?.days_until === 1 ? '¡Mañana!' :
                   `En ${predictions.next_period?.days_until} días`}
                </p>
              ) : (
                <p className="text-2xl font-orbitron text-red-400">
                  {predictions.next_period?.days_until} días
                </p>
              )}
              <p className="text-sm text-slate-300">
                {new Date(predictions.next_period?.start + 'T12:00:00').toLocaleDateString('es-ES', { 
                  day: 'numeric', 
                  month: 'short' 
                })}
              </p>
            </div>
            
            {/* Ovulation Window */}
            <div className="p-4 rounded-lg bg-gradient-to-r from-yellow-500/10 to-cyan-500/10 border border-yellow-500/20 relative">
              <p className="text-xs text-slate-400 mb-1">Ovulación estimada</p>
              <p className="text-lg font-orbitron text-yellow-400">
                {new Date(predictions.ovulation?.date + 'T12:00:00').toLocaleDateString('es-ES', { 
                  day: 'numeric', 
                  month: 'short' 
                })}
              </p>
              <p className="text-xs text-cyan-400 mt-1">
                Ventana fértil: {new Date(predictions.ovulation?.fertile_window_start + 'T12:00:00').toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })} - {new Date(predictions.ovulation?.fertile_window_end + 'T12:00:00').toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
              </p>
              {/* Calendar Download Button */}
              <button
                onClick={handleFertileCalendarDownload}
                disabled={downloadingCalendar}
                className="mt-3 w-full px-3 py-2 rounded-lg text-xs font-rajdhani transition-all flex items-center justify-center gap-2 bg-gradient-to-r from-cyan-500/20 to-yellow-500/20 border border-cyan-500/30 text-cyan-400 hover:border-cyan-500/50 hover:from-cyan-500/30 hover:to-yellow-500/30 disabled:opacity-50"
              >
                <Calendar className="w-4 h-4" />
                {downloadingCalendar ? 'Descargando...' : 'Añadir a mi calendario'}
              </button>
            </div>
          </div>
          
          {/* Cycle Stats */}
          <div className="mt-4 pt-4 border-t border-white/10 flex flex-wrap gap-4 text-xs text-slate-400">
            <span>Ciclo promedio: <strong className="text-white">{predictions.cycle_stats?.average_length} días</strong></span>
            <span>Período promedio: <strong className="text-white">{predictions.cycle_stats?.average_period_length} días</strong></span>
            <span>Períodos registrados: <strong className="text-white">{predictions.cycle_stats?.periods_tracked}</strong></span>
          </div>
          
          {predictions.cycle_stats?.periods_tracked < 3 && (
            <p className="text-xs text-slate-500 mt-3 italic">
              💡 Registra más períodos para mejorar la precisión de las predicciones
            </p>
          )}
        </GlassCard>
      )}
      
      {/* Setup Period - Show if no predictions */}
      {!predictions && !loading && (
        <GlassCard className="p-6 text-center">
          <Calendar className="w-12 h-12 text-pink-400 mx-auto mb-4 opacity-50" />
          <h3 className="font-orbitron text-lg text-white mb-2">
            Configura tu Ciclo
          </h3>
          <p className="text-slate-400 text-sm mb-4 max-w-md mx-auto">
            Para ver predicciones de tu próximo período y ventana fértil, registra cuando comenzó tu último período.
          </p>
          <Button
            onClick={() => setShowPeriodSetup(true)}
            className="bg-pink-500/20 border border-pink-500/50 text-pink-400 hover:bg-pink-500/30"
          >
            Configurar fecha del período
          </Button>
        </GlassCard>
      )}

      {/* Analysis Section */}
      {(analysis?.entries_count > 0 || aiInsights) && (
        <CycleAnalysis analysis={analysis} aiInsights={aiInsights} />
      )}

      {/* Entry Form Modal */}
      <AnimatePresence>
        {showEntryForm && (
          <EntryForm
            date={selectedDate}
            existingEntry={selectedEntry}
            symptoms={symptoms}
            flowOptions={flowOptions}
            onSave={handleSaveEntry}
            onClose={() => setShowEntryForm(false)}
          />
        )}
      </AnimatePresence>
      
      {/* Period Setup Modal */}
      <AnimatePresence>
        {showPeriodSetup && (
          <PeriodSetupModal
            onSave={async (data) => {
              try {
                await axios.post(`${API}/cycle/set-period-start`, data);
                toast.success("Configuración guardada");
                fetchData();
                setShowPeriodSetup(false);
              } catch (error) {
                toast.error("Error al guardar");
              }
            }}
            onClose={() => setShowPeriodSetup(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};
